import { Experiment } from '@ms/yammer-data/dist/state/types';
import { AnalyticsEventName, reportUsage } from '@ms/yammer-data/dist/telemetry';
import { useAndReportExperimentTreatment } from '@ms/yammer-hooks/dist/experiments';
import { useIsTeamsTab } from 'hooks/selectors';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useIsDeepLinkPage } from './useIsDeepLinkPage';

type UseBackButtonProps = () => {
  readonly goBack: () => void;
  readonly showButton: boolean;
};

const rootPaths = [
  /^\/admin\/?$/,
  /^\/articles\/new$/,
  /^\/discover-groups\/explore((\/\w+)+|\/?)$/,
  /^\/feed((\/\w+)+|\/?)$/,
  /^\/inbox((\/\w+)+|\/?)$/,
  /^\/leaders\/?$/,
  /^\/leadership-corner((\/\w+)+|\/?)$/,
  /^\/storylines((\/\w+)+|\/?)$/,
  /^\/questions\/?$/,
  /^\/user-settings((\/\w+)+|\/?)$/,
  /^\/events\/[^/]+(\/questions|\/unanswered)?$/,
];

export const useBackButtonProps: UseBackButtonProps = () => {
  const history = useHistory();
  const location = useLocation();
  const [showButton, setShowButton] = useState(false);
  const isBackButtonEnabled = useAndReportExperimentTreatment(Experiment.BackButton) === true;
  const isDeepLinkPage = useIsDeepLinkPage(location);
  const isTeamsTab = useIsTeamsTab();
  const hasHistory = history.length > 1;

  const goBack = useCallback(() => {
    reportUsage({
      eventName: AnalyticsEventName.VivaEngageBackButtonClicked,
      eventProperties: { backFrom: window.location.href },
    });
    history.goBack();
  }, [history]);

  useEffect(() => {
    if (!isBackButtonEnabled || isDeepLinkPage || isTeamsTab || !hasHistory) {
      setShowButton(false);

      return;
    }

    const path = location.pathname;
    if (rootPaths.some((rootPath) => rootPath.test(path))) {
      setShowButton(false);
    } else {
      setShowButton(true);
    }
  }, [location, isBackButtonEnabled, isDeepLinkPage, hasHistory, isTeamsTab]);

  return {
    goBack,
    showButton,
  };
};
